package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectAccentKey
import dev.lingvo.shared.ProjectIconKey
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LChevronDownIcon
import dev.lingvo.site.ds.LPresencePhase
import dev.lingvo.site.ds.LProjectMark
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.rememberLPresence
import dev.lingvo.site.ds.toSitePalette
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.events.SyntheticKeyboardEvent
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.MouseEvent

/**
 * A crumb that opens a list of real links.
 *
 * Deliberately not [dev.lingvo.site.ds.LMenu]: its rows are buttons dispatching by index, and the
 * whole point of this control is that a row is an anchor somebody can middle-click.
 *
 * Extracted out of `AppTrail.kt` so the editor's locale picker (`EditorHeader.kt`) can reuse real
 * anchors, arrow-key navigation and Escape-to-close rather than growing a second popover — the
 * trail and the editor header are the two callers, and a second implementation would drift.
 */
@Composable
internal fun Picker(
    label: String,
    ariaLabel: String,
    // What the id is derived from — a workspace or project slug, prefixed by which kind it is so
    // the two pickers can never collide. Deterministic rather than `Random.nextInt`: this id is
    // read back by `aria-controls`, and a value that changes on every recomposition is a value a
    // test (or a person scripting against the DOM) cannot rely on.
    idSeed: String,
    open: Boolean,
    onOpenChange: (Boolean) -> Unit,
    width: Int = WORKSPACE_PICKER_WIDTH,
    // Null for a workspace name, where no source language is known. `dir="auto"` still applies —
    // it fixes RTL layout without claiming a language.
    labelLang: String? = null,
    // True where the picker is an eyebrow above a title rather than a crumb in a row.
    //
    // **The trigger's own padding is what puts its label 8px right of the H1 below it.** The
    // padding is correct for the trail, where the hover surface has to have a shape and the crumbs
    // beside it are inset by the same amount; it is wrong under a heading, where the eyebrow, the
    // title and everything below them share one left edge and an 8px step reads as two columns.
    // Pulling the control back by exactly its inline padding keeps the hover and focus surface the
    // size a pointer expects and puts the *text* where the reader expects it. `margin-inline-start`
    // rather than `margin-left`, because in an RTL locale the offset is on the other side.
    //
    // **The offset is paid back on the other side, and it has to be.** A lone
    // `margin-inline-start: -8px` cost the label the eight pixels it was meant to move. Every box
    // between here and the heading is shrink-to-fit — the picker's own wrapper, and the page
    // header's eyebrow slot above it — and a negative margin comes out of the max-content width
    // each of them resolves to. The button's `max-width: 100%` then resolved against a box eight
    // pixels narrower than the button had asked for, and the label — `overflow: hidden` with an
    // ellipsis, because in the trail it must clip — spent the difference: "German ⌄" was drawn
    // "Germ… ⌄" with a thousand pixels of empty row beside it, at 1440 and at 390, in both schemes.
    // Moving the margin to the wrapper only moved the same subtraction one box up.
    //
    // A matching positive `margin-inline-end` is what fixes it: the two cancel in every intrinsic
    // width this chain computes, so no ancestor loses anything, while the control still starts
    // eight pixels to the inline-start of where it otherwise would. Logical properties both, so an
    // RTL locale offsets the other way.
    flushStart: Boolean = false,
    leading: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    val pickerId = remember(idSeed) { "lingvo-picker-$idSeed" }
    // Set only by `Esc`, and it is the difference between two closes that must not behave alike:
    // `Esc` returns focus to the trigger, `Tab` leaves and must not be dragged back. Closing by
    // scrim is a pointer gesture and moves no focus either.
    var returnFocusToTrigger by remember { mutableStateOf(false) }
    // Without this the very first composition — with the popover shut — would "restore" focus to a
    // trigger nobody touched, stealing the caret on page load.
    var everOpened by remember { mutableStateOf(false) }
    var instantMotion by remember { mutableStateOf(false) }
    val presence = rememberLPresence(open, Motion.FastMs, settleImmediately = instantMotion)

    LaunchedEffect(open, presence.rendered, presence.phase) {
        if (open && presence.rendered) {
            everOpened = true
            // Focus lands on the row you are already on, so `↓` moves from where you are rather
            // than from the top of a list you have to re-read.
            val popover = document.getElementById("$pickerId-popover")
            val row = popover?.querySelector("[$ROW_CURRENT_ATTR]") as? HTMLElement
                ?: popover?.querySelector("[$ROW_ATTR]") as? HTMLElement
            row?.focus()
        } else if (!open && !presence.rendered && everOpened && returnFocusToTrigger) {
            returnFocusToTrigger = false
            (document.getElementById("$pickerId-trigger") as? HTMLElement)?.focus()
        }
    }

    // `min-width: 0` so this flex child may shrink; without it the trigger inside keeps its full
    // content width and the trail overflows into whatever sits beside it.
    Div(
        Modifier
            .position(Position.Relative)
            .display(DisplayStyle.InlineBlock)
            .minWidth(0.px)
            .toAttrs(),
    ) {
        Button(
            AppTrailTriggerStyle.toModifier()
                .thenIf(flushStart) {
                    Modifier.styleModifier {
                        // The control moves inline-start by its own padding so the *label* lands on
                        // the content column's left edge, and gives the same eight pixels back at
                        // the other end so no shrink-to-fit ancestor is shortened by the move.
                        // See `flushStart`.
                        property("margin-inline-start", "-$AppTrailTriggerInlinePadding")
                        property("margin-inline-end", AppTrailTriggerInlinePadding.toString())
                        // 220px is the trail's cap, and the trail earns it: there the trigger
                        // shares a 56px bar with a search field it must not paint over. An eyebrow
                        // has the content column to itself, so the cap could only ever cost a long
                        // language name its last glyphs.
                        property("max-width", "100%")
                    }
                }
                .toAttrs {
                    id("$pickerId-trigger")
                    type(ButtonType.Button)
                    attr("aria-haspopup", "true")
                    attr("aria-expanded", open.toString())
                    attr("aria-controls", "$pickerId-popover")
                    attr("aria-label", ariaLabel)
                    onClick { event ->
                        instantMotion = (event.nativeEvent as MouseEvent).detail == 0
                        onOpenChange(!open)
                    }
                },
        ) {
            leading?.invoke()
            Span(
                AppTrailTriggerLabelStyle.toModifier().toAttrs {
                    attr("dir", "auto")
                    labelLang?.let { attr("lang", it) }
                },
            ) { Text(label) }
            LChevronDownIcon(AppTrailChevronStyle.toModifier())
        }
        if (presence.rendered) {
            val exiting = presence.phase == LPresencePhase.EXITING
            // **An outside click has to close this.** Without a scrim the popover stayed open behind
            // whatever you pressed next, and two of them could stand open at once — the workspace
            // list over the project list, both claiming to say where you are.
            Div(
                Modifier
                    .position(Position.Fixed)
                    .styleModifier {
                        property("inset", "0")
                    }
                    // The named picker layer sits below its surface and above ordinary shell content,
                    // so dismissal cannot leak into the control beneath it.
                    .zIndex(AppLayer.PickerScrim)
                    .attr("data-presence", presence.phase.attributeValue)
                    .then(if (exiting) Modifier.attr("inert", "").styleModifier { property("pointer-events", "none") } else Modifier)
                    .toAttrs {
                        attr("aria-hidden", "true")
                        onClick { onOpenChange(false) }
                    },
            )
            Div(
                AppTrailPopoverStyle.toModifier().styleModifier {
                    property("--picker-max-width", "${width}px")
                    if (instantMotion) property("transition", "none")
                }.toAttrs {
                    id("$pickerId-popover")
                    attr("data-presence", presence.phase.attributeValue)
                    if (exiting) {
                        attr("inert", "")
                        attr("aria-hidden", "true")
                    }
                    attr(ROW_CONTAINER_ATTR, "")
                    // Any activation inside closes it: the row is a link, so navigation follows.
                    onClick { event ->
                        instantMotion = (event.nativeEvent as MouseEvent).detail == 0
                        onOpenChange(false)
                    }
                    // **The keys a list of links owes its reader.** There were none: `Esc` did
                    // nothing, the arrows scrolled the page behind the open popover, and `Tab` left
                    // it standing open over the route you had just moved to.
                    onKeyDown { event ->
                        when (event.key) {
                            "Escape" -> {
                                event.preventDefault()
                                instantMotion = true
                                returnFocusToTrigger = true
                                onOpenChange(false)
                            }
                            // Not a focus trap — it is a menu of links. Tab closes and moves on,
                            // and deliberately does not preventDefault so the browser's own next
                            // stop is where focus lands.
                            "Tab" -> {
                                instantMotion = true
                                onOpenChange(false)
                            }
                            else -> moveRowFocus(event)
                        }
                    }
                },
            ) {
                // A Kobweb `Column` is a flex container with `align-items: flex-start` by default,
                // so every row — `display: block` in `AppTrailRowStyle`, but still a flex *item*
                // here — shrank to its own content width instead of filling the popover: a 110px
                // popover held rows 84-100px wide with dead space beside each one. Jetpack Compose's
                // `Alignment.Horizontal` has no `Stretch` value (unlike CSS `align-items`), so the
                // fill is stated on the row itself — see `AppTrailRowStyle`'s `width: 100%`.
                Column(Modifier.gap(2.px), content = { content() })
            }
        }
    }
}

/**
 * `↑` `↓` `Home` `End` across the rows, wrapping.
 *
 * Reads the rows out of the DOM rather than being handed them, because the two pickers build their
 * lists differently — one from memberships, one from a directory that may be loading, empty or
 * failed — and a handler that took an index would have to be told about every one of those shapes.
 */
internal fun moveRowFocus(event: SyntheticKeyboardEvent) {
    val key = event.key
    if (key != "ArrowDown" && key != "ArrowUp" && key != "Home" && key != "End") return
    val focused = event.target as? HTMLElement ?: return
    val popover = focused.closest("[$ROW_CONTAINER_ATTR]") ?: return
    val nodes = popover.querySelectorAll("[$ROW_ATTR]")
    val rows = buildList {
        for (index in 0 until nodes.length) (nodes.item(index) as? HTMLElement)?.let(::add)
    }
    if (rows.isEmpty()) return
    val here = rows.indexOf(focused).coerceAtLeast(0)
    event.preventDefault()
    when (key) {
        "ArrowDown" -> rows[(here + 1) % rows.size]
        "ArrowUp" -> rows[(here - 1 + rows.size) % rows.size]
        "Home" -> rows.first()
        else -> rows.last()
    }.focus()
}

@Composable
internal fun PickerRow(
    label: String,
    href: String,
    current: Boolean,
    onActivate: () -> Unit,
    note: String? = null,
    // Null for a workspace name or a static row ("New project", "All projects"), where no source
    // language is known. `dir="auto"` still applies — it fixes RTL layout without claiming a
    // language.
    labelLang: String? = null,
    iconKey: ProjectIconKey? = null,
    accentKey: ProjectAccentKey? = null,
    /**
     * The project this row identifies, so its uploaded mark can be drawn instead of the preset one.
     *
     * Optional because most rows are not projects at all — a workspace, a static "New project" row,
     * a locale. When [iconKey] and [accentKey] are given without a slug, the row falls back to the
     * preset mark rather than silently drawing nothing.
     */
    slug: String? = null,
    hasCustomIcon: Boolean = false,
    /**
     * The row's mark, drawn in the column after the reserved tick.
     *
     * The command palette draws every destination's [dev.lingvo.site.nav.NavGlyph] here, so a row in
     * the palette and the same row in the rail carry the same drawing. Null keeps the row exactly as
     * it was — a project mark ([iconKey]/[accentKey]) or nothing at all.
     */
    glyph: (@Composable () -> Unit)? = null,
) {
    val palette = ColorMode.current.toSitePalette()
    Link(
        path = href,
        // **The side effect rides the anchor itself.** Hanging it off an inner span meant keyboard
        // activation — and any click landing on the row's padding rather than its text — navigated
        // without running it, so choosing another workspace moved you to /app while the selection
        // stayed on the workspace you had just left.
        modifier = AppTrailRowStyle.toModifier()
            .then(if (current) Modifier.backgroundColor(palette.accentSubtle).color(palette.accent) else Modifier)
            .attr("aria-current", if (current) "true" else "false")
            // Read back by the picker's keyboard handling: which elements are rows, and which one
            // to open on. Marked in the DOM rather than passed down, because the two pickers build
            // their lists from different sources.
            .attr(ROW_ATTR, "")
            .then(if (current) Modifier.attr(ROW_CURRENT_ATTR, "") else Modifier)
            .onClick { onActivate() }
            .cursor(Cursor.Pointer),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) {
        Row(Modifier.gap(Space.SM)) {
            // **The check column is reserved, not conditional.** Drawing the tick only on the
            // current row shifted every other row's name 24px to the left, so the list re-aligned
            // itself as you moved between workspaces. `accentSubtle` behind the row is 1.23:1 — the
            // tick at 5.44:1 is what actually says "this one".
            Span(
                Modifier
                    .iconSize(IconSize.MD)
                    .minWidth(IconSize.MD)
                    .color(palette.accent)
                    .toAttrs {
                        attr("aria-hidden", "true")
                        attr("data-picker-check-column", "")
                    },
            ) {
                if (current) LCheckIcon()
            }
            glyph?.let {
                Span(
                    Modifier
                        .iconSize(IconSize.MD)
                        .minWidth(IconSize.MD)
                        .color(palette.textSecondary)
                        .toAttrs { attr("aria-hidden", "true") },
                ) {
                    it()
                }
            }
            if (iconKey != null && accentKey != null) {
                if (slug != null) {
                    ProjectIcon(
                        slug = slug,
                        name = label,
                        iconKey = iconKey,
                        accentKey = accentKey,
                        hasCustomIcon = hasCustomIcon,
                        modifier = Modifier.width(24.px).minWidth(24.px).height(24.px),
                    )
                } else {
                    LProjectMark(
                        name = label,
                        iconKey = iconKey,
                        accentKey = accentKey,
                        modifier = Modifier.width(24.px).minWidth(24.px).height(24.px),
                    )
                }
            }
            Column(Modifier.minWidth(0.px).gap(1.px).styleModifier { property("flex", "1 1 auto") }) {
                Span(
                    Modifier.styleModifier {
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }.toAttrs {
                        attr("data-picker-label", "")
                        attr("dir", "auto")
                        labelLang?.let { attr("lang", it) }
                    },
                ) { Text(label) }
                note?.let {
                    Span(
                        // 13.5, the meta step. At `Micro` (10.5) the note was smaller than any
                        // other text in the shell and read as a footnote rather than as the
                        // sentence that tells you where the row lands.
                        Modifier.fontSize(TypeScale.Small).color(palette.textMuted).toAttrs(),
                    ) {
                        Text(it)
                    }
                }
            }
        }
    }
}

@Composable
internal fun PickerNote(text: String) {
    Span(
        Modifier
            .padding(topBottom = Space.XS, leftRight = Space.SM)
            .fontSize(TypeScale.Small)
            .color(ColorMode.current.toSitePalette().textSecondary)
            .toAttrs(),
    ) {
        Text(text)
    }
}

/**
 * How the picker's keyboard handling finds its rows in the DOM.
 *
 * Attributes rather than ids, because the rows are anchors built by two different callers from three
 * different sources — memberships, a directory that may be loading, and a create row that only some
 * roles see — and any scheme that numbered them would have to be told about each of those shapes.
 *
 * Internal, not private: the command palette (`CommandPalette.kt`) marks its own rows and container
 * with these so it can call [moveRowFocus] directly, rather than duplicating the literal attribute
 * names and risking the two ever disagreeing.
 */
internal const val ROW_ATTR = "data-picker-row"
internal const val ROW_CURRENT_ATTR = "data-picker-current"
internal const val ROW_CONTAINER_ATTR = "data-picker-popover"

/** Three workspaces, one line each. */
private const val WORKSPACE_PICKER_WIDTH = 320
